import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Button, CircularProgress, Container, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../utils/env';

import Logo from '../components/logo';

// ----------------------------------------------------------------------

// components
import Iconify from '../components/iconify';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '20px',
  padding: theme.spacing(8, 0),
}));

// ----------------------------------------------------------------------

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');


  // Extract the token from the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    if (tokenFromUrl) {
    const decodedToken = decodeURIComponent(tokenFromUrl).replace(/\s+/g, '+');
      setToken(decodedToken);
      console.log(decodedToken);
      
      const emailFromStorage = localStorage.getItem('resetEmail');
      if (emailFromStorage) {
        setEmail(emailFromStorage);
      } else {
        toast.error('No email found for password reset');
        navigate('/forgot-password');
      }
    } else {
      toast.error('Invalid token');
      navigate('/forgot-password');
    }
  }, [location, navigate]);

  const handleSubmit = async () => {
    if (password !== passwordConfirm) {
      setError("Passwords don't match");
      toast.error("Passwords don't match");
      setTimeout(() => {
        setError(null);
      }, 3000);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/Account/reset-password`, {
        userEmail: email,
        newPassword: password,
        token,
      });

      if (response.data.success) {
        toast.success('Password reset successfully');
        localStorage.removeItem('resetEmail'); // Clear email from local storage
        navigate('/login');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title> Reset Password | CaaS </title>
      </Helmet>

      <StyledRoot>
        <Logo
          sx={{
            position: 'fixed',
            top: { xs: 16, sm: 24, md: 40 },
            left: { xs: 16, sm: 24, md: 40 },
          }}
        />

        <Container maxWidth="sm">
          <StyledContent>
            <Typography>
              <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', color: '#000080' }}>
                Reset Password
              </Typography>
              <Typography m={0} variant="subtitle1" color={'grey'} textAlign={'center'}>
                Kindly enter your new pasword below
              </Typography>
            </Typography>
            <TextField
              sx={{
                // change the focus color
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#000080',
                },
                // the label color
                '& .MuiInputLabel-outlined.Mui-focused': {
                  color: '#000080',
                },
              }}
              fullWidth
              name="password"
              label="Enter new Passord"
              value={password}
              type={showPassword ? 'text' : 'password'}
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              sx={{
                // change the focus color
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#000080',
                },
                // the label color
                '& .MuiInputLabel-outlined.Mui-focused': {
                  color: '#000080',
                },
              }}
              value={passwordConfirm}
              fullWidth
              type={showPasswordConfirm ? 'text' : 'password'}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} edge="end">
                      <Iconify icon={showPasswordConfirm ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              name="confirmPassword"
              label="Confirm new Passord"
              error={error !== null}
              className={error !== null ? 'shaky' : ''}
            />
            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: '#000080',

                '&:hover': {
                  backgroundColor: '#000090',
                },
              }}
              onClick={handleSubmit}
            >
          
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset'}
            </Button>
          </StyledContent>
        </Container>
      </StyledRoot>
    </>
  );
}
